import { createSlice, createAsyncThunk, createSelector, PayloadAction } from "@reduxjs/toolkit";
import { collection, getDocs, addDoc, doc, deleteDoc, updateDoc, where, query, getDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/firebase";
import { Menu, Organization, Template } from "@/types";
import { RootState } from "@/store/store";

export type MenusState = Record<string, Menu>;
type MenuId = Menu["id"];

const objectToState = (state: MenusState, payload?: Menu) => {
  if (!payload) return state;
  return { ...state, [payload.id]: payload };
};

const arrayToState = (state: MenusState, payload?: Menu[]) => {
  if (!payload) return state;

  const draft = payload.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.id]: curr,
    }),
    {} as MenusState
  );

  return { ...state, ...draft };
};

const initialState: MenusState = {};

export const fetchMenuById = createAsyncThunk("menus/fetchMenuById", async (menuId: MenuId) => {
  try {
    const docRef = doc(db, `menus/${menuId}`);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) return;
    return { ...docSnap.data(), id: menuId } as Menu;
  } catch (error) {
    console.error(error);
  }
});

export const fetchMenus = createAsyncThunk("menus/fetchMenus", async () => {
  try {
    const collectionRef = collection(db, "menus");
    const querySnapshot = await getDocs(collectionRef);

    const documents: Menu[] = [];
    querySnapshot.forEach((doc) => {
      documents.push({ ...doc.data(), id: doc.id } as Menu);
    });
    return documents;
  } catch (error) {
    console.error(error);
  }
});

//

export const fetchMenusByOrganizationId = createAsyncThunk("menus/MenusByOrganizationId", 
  async(organizationid: string) => {
    try {
      const menuRef = collection(db, "menus");
      const menuByOrgId = query(menuRef, where("organization", "==", organizationid ));
      const querySnapshot = await getDocs(menuByOrgId);

      const documents: Menu[] = [];
      querySnapshot.forEach((doc) => {
        documents.push({...doc.data(), id: doc.id} as Menu);
      })
      return documents;
    } catch (error) {
      console.error(error)
    }
  });

//


export const getMenusByLoggedInUser = createAsyncThunk("menus/MenusByLoggedInUser", async (_, { getState }) => {
  try {
    const { auth } = getState() as RootState;
    const menuRef = collection(db, "menus");
    const q = query(menuRef, where("createdBy", "==", auth?.user?.uid));

    const querySnapshot = await getDocs(q);

    const documents: Menu[] = [];
    querySnapshot.forEach((doc) => {
      documents.push({ ...doc.data(), id: doc.id } as Menu);
    });
    return documents;
  } catch (error) {
    console.error(error);
  }
});

export const addMenu = createAsyncThunk("menus/addMenu", async (menu: Menu, { getState }) => {
  const { auth } = getState() as RootState;
  try {
    const collectionRef = collection(db, "menus");
    const doc = await addDoc(collectionRef, {
      ...menu,
      createdBy: auth?.user?.uid,
    });
    return { ...menu, id: doc.id };
  } catch (error) {
    console.error(error);
  }
});

export const deleteMenu = createAsyncThunk("menus/deleteMenu", async (id: MenuId) => {
  const docRef = doc(db, `menus/${id}`);
  await deleteDoc(docRef);
  return id;
});

export const updateMenu = createAsyncThunk("menus/updateMenu", async (menu: Partial<Menu>) => {
  try {
    const docRef = doc(db, `menus/${menu.id}`);
    await updateDoc(docRef, { ...menu });
    return menu;
  } catch (error) {
    console.error(error);
  }
});

// Templates
interface ApplyTemplateToMenu {
  templateId: Template["id"];
  menuId: string;
}
// ✅ Checked
export const applyTemplateToMenu = createAsyncThunk(
  "templates/applyTemplateToMenu",
  async ({ menuId, templateId }: ApplyTemplateToMenu, { getState }) => {
    const { menus, templates, auth } = getState() as RootState;
    const menu = menus[menuId];
    const template = templates[templateId];

    if (
      !menu?.title
      || !templateId
      || !template?.data 
      || !auth?.user?.uid
    ) return;

    try {
      const payload = {
        title: menu.title,
        template: templateId,
        data: template.data,
        lastUpdatedBy: auth.user.uid,
        lastUpdatedAt: serverTimestamp(),
      };

      const docRef = doc(db, `menus/${menuId}`);
      await updateDoc(docRef, payload);
      return payload;
    } catch (error) {
      console.error(error);
    }
  }
);

const menuSlice = createSlice({
  name: "menus",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchMenuById.fulfilled, (state, action: PayloadAction<Menu | undefined>) => {
      return objectToState(state, action?.payload);
    });
    builder.addCase(fetchMenus.fulfilled, (state, action: PayloadAction<Menu[] | undefined>) => {
      return arrayToState(state, action?.payload);
    });
    //
    builder.addCase(fetchMenusByOrganizationId.fulfilled, (state, action: PayloadAction<Menu[] | undefined>) => {
      return arrayToState(state, action?.payload);
    })
    //
    builder.addCase(getMenusByLoggedInUser.fulfilled, (state, action: PayloadAction<Menu[] | undefined>) => {
      return arrayToState(state, action?.payload);
    });
    builder.addCase(addMenu.fulfilled, (state, action: PayloadAction<Menu | undefined>) => {
      return objectToState(state, action?.payload);
    });
    builder.addCase(deleteMenu.fulfilled, (state, action: PayloadAction<MenuId | undefined>) => {
      if (action.payload) {
        delete state[action.payload];
        return state;
      }
    });
    builder.addCase(updateMenu.fulfilled, (state, action: PayloadAction<Menu | undefined>) => {
      return objectToState(state, action?.payload);
    });
    // Templates
    builder.addCase(applyTemplateToMenu.fulfilled, (state, action: PayloadAction<Menu | undefined>) => {
      return objectToState(state, action?.payload);
    });
  },
});

export const stateSelector = (state: RootState) => state;
export const menusSelector = (state: RootState) => state.menus;

export const menuListSelector = createSelector(menusSelector, (menus) => Object.values(menus));

export const menuListByOrgSelector = (organizationId: Organization["id"]) =>
  createSelector(menusSelector, (menus) => Object.values(menus).filter((menu) => menu.organization === organizationId));

export const menuSelector = (id?: MenuId) =>
  createSelector(stateSelector, (state) => {
    if (!id) return null;
    const menu = state.menus[id];
    if (!menu) return null;
    return menu;
  });

export default menuSlice.reducer;
